/* Attendance Dashboard — branch workforce presence for the selected period.

   Timesheet (tasks) and the employee Attendance step (one person's punches)
   stay elsewhere. This page matches Day Attendance's population after the
   header branch. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, BarChart3, CalendarDays, Clock, Timer, UserCheck, UserX,
    ClipboardList, Scale,
} from "lucide-react";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { cn } from "../../utils/tailwindUtils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { ATTENDANCE_DASHBOARD } from "../../constants/api";
import { BRANCH_FILTER_CHANGED_EVENT } from "../../utils/branchSession";

const PERIODS = [
    { value: "today", label: "Today" },
    { value: "month", label: "This month" },
    { value: "30", label: "30 days" },
    { value: "90", label: "90 days" },
];

const STATUS_BAR = {
    present: "bg-emerald-500",
    tp: "bg-emerald-600",
    absent: "bg-rose-500",
    ta: "bg-rose-600",
    leave: "bg-violet-500",
    "on-duty": "bg-sky-500",
    wfh: "bg-cyan-500",
    "week-off": "bg-slate-400",
    holiday: "bg-amber-500",
    compensation: "bg-pink-500",
};

const ISSUE_LEGEND = [
    {
        key: "single_punch",
        label: "Single punch",
        chip: "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
        rule: "Check-in with no check-out on a working day. Today's still-open check-in is skipped.",
    },
    {
        key: "pending_penalty",
        label: "Penalty pending",
        chip: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
        rule: "Short duty or late penalty not yet accepted.",
    },
    {
        key: "pending_correction",
        label: "Correction pending",
        chip: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        rule: "An attendance correction still waiting.",
    },
    {
        key: "repeat_late",
        label: "Repeat late",
        chip: "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
        rule: "2 or more late days in this period (10-minute grace, same as Home).",
    },
    {
        key: "late",
        label: "Late · Today",
        chip: "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
        rule: "Today only, and only if that person has a single late day.",
    },
];

const personLabel = (employee) => {
    const name = employee?.employee_name || "this person";
    const code = employee?.employee_id;
    return code ? `${name} / ${code}` : name;
};

const latestTooltip = (row) => {
    const who = personLabel(row.employee);
    const days = row.days || 1;
    if (row.issue === "single_punch") {
        return days > 1
            ? `This row is the same person (${who}) with ${days} days in this period where they checked in and never checked out.`
            : `This person (${who}) checked in and never checked out on this date.`;
    }
    if (row.issue === "pending_penalty") {
        return days > 1
            ? `This row is the same person (${who}) with ${days} days in this period where a short duty or late penalty is still pending review.`
            : `This person (${who}) has a short duty or late penalty still pending review on this date.`;
    }
    if (row.issue === "pending_correction") {
        return days > 1
            ? `This row is the same person (${who}) with ${days} pending correction requests in this period.`
            : `This person (${who}) has an attendance correction still waiting on this date.`;
    }
    if (row.issue === "repeat_late") {
        return `This row is the same person (${who}) with ${days} late days in this period.`;
    }
    return `This person (${who}) was late on this date.`;
};

const Tile = ({ Icon, label, value, suffix, tone = "default", hint }) => (
    <div className="rounded-lg border border-gray-200 bg-white p-3.5 dark:border-gray-700 dark:bg-gray-800/40">
        <div className="flex items-center gap-2">
            <span
                className={cn(
                    "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                    tone === "danger"
                        ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300"
                        : tone === "success"
                            ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300"
                            : "bg-sidebar-primary/10 text-sidebar-primary",
                )}
            >
                <Icon className="h-4 w-4" />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
        </div>
        <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
            {value === null || value === undefined ? "—" : value}
            {value !== null && value !== undefined && suffix && (
                <span className="ml-1 text-sm font-normal text-muted-foreground">{suffix}</span>
            )}
        </p>
        {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
);

const SectionLabel = ({ children }) => (
    <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        {children}
    </p>
);

const DistributionBars = ({ rows, total, colorFor, empty }) => {
    if (!rows.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
    return (
        <div className="space-y-2">
            {rows.map((row) => {
                const pct = total ? Math.round((row.count * 100) / total) : 0;
                return (
                    <div key={row.key} className="flex items-center gap-3">
                        <span className="w-44 shrink-0 truncate text-xs text-gray-700 dark:text-gray-300">
                            {row.label}
                        </span>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                            <div
                                className={cn("h-full rounded-full", colorFor(row))}
                                style={{ width: `${Math.max(pct, row.count ? 2 : 0)}%` }}
                            />
                        </div>
                        <span className="w-20 shrink-0 text-right text-xs font-medium text-gray-700 dark:text-gray-300">
                            {row.right || `${row.count} · ${pct}%`}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const fmtDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatHours = (seconds) => {
    if (seconds === null || seconds === undefined) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return `${h}h ${m}m`;
};

const AttendanceDashboard = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [period, setPeriod] = useState("month");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(ATTENDANCE_DASHBOARD, { period });
            if (res?.status === "success") setData(res.data);
            else ToastMsg("error", res?.message || "Could not load the dashboard");
        } catch (error) {
            ToastMsg("error", error.message || "Could not load the dashboard");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onBranchChange = () => load();
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchChange);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchChange);
    }, [load]);

    const openAttention = (row) => {
        if (!row?.href) return;
        const date = row.date;
        const state = { date, fromDate: date, toDate: date };
        if (row.issue === "single_punch") state.issueType = "single_punch";
        navigate(row.href, { state });
    };

    const tiles = data?.tiles || {};
    const recorded = tiles.recorded || 0;

    const statusRows = (data?.by_status || []).map((row) => ({
        key: row.status,
        label: row.status_label || row.status,
        count: row.count,
        status: row.status,
    }));
    const departmentRows = (data?.by_department || []).map((row) => ({
        key: row.department_name,
        label: row.department_name,
        count: row.present_percent,
        right: `${row.present} · ${row.present_percent}%`,
        presentPercent: row.present_percent,
    }));

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={BarChart3}
                title="Attendance Dashboard"
                subtitle="Presence, late arrivals and punch exceptions for this branch"
            />

            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 px-3">
                <SegmentSwitch options={PERIODS} value={period} onChange={setPeriod} />
                <Button variant="outline" size="sm" onClick={() => navigate("/attendance/dayattendance")}>
                    <CalendarDays className="h-4 w-4" /> Open Day Attendance
                </Button>
            </div>

            {loading && !data ? (
                <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
            ) : (
                <div className="space-y-4 px-3 pb-6">
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <Tile
                            Icon={UserCheck}
                            label="Present"
                            value={tiles.present_percent}
                            suffix="%"
                            tone="success"
                            hint={tiles.present != null ? `${tiles.present} present days` : undefined}
                        />
                        <Tile Icon={UserX} label="Absent" value={tiles.absent} />
                        <Tile Icon={ClipboardList} label="On leave" value={tiles.leave} />
                        <Tile
                            Icon={Clock}
                            label="Late"
                            value={tiles.late}
                            tone={tiles.late ? "danger" : "default"}
                        />
                        <Tile
                            Icon={AlertTriangle}
                            label="Exceptions"
                            value={tiles.exceptions}
                            tone={tiles.exceptions ? "danger" : "default"}
                            hint="Single punch / missing check-out"
                        />
                        <Tile
                            Icon={Scale}
                            label="Penalties pending"
                            value={tiles.penalties_pending}
                            tone={tiles.penalties_pending ? "danger" : "default"}
                        />
                        <Tile
                            Icon={ClipboardList}
                            label="Corrections pending"
                            value={tiles.corrections_pending}
                        />
                        <Tile
                            Icon={Timer}
                            label="Avg hours"
                            value={formatHours(tiles.avg_working_seconds)}
                            hint="On present days"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <div className="rounded-lg border border-gray-200 p-3.5 dark:border-gray-700">
                            <SectionLabel>By status</SectionLabel>
                            <DistributionBars
                                rows={statusRows}
                                total={recorded}
                                empty="No attendance recorded for this period."
                                colorFor={(row) => STATUS_BAR[row.status] || "bg-sidebar-primary"}
                            />
                        </div>
                        <div className="rounded-lg border border-gray-200 p-3.5 dark:border-gray-700">
                            <SectionLabel>By department</SectionLabel>
                            <DistributionBars
                                rows={departmentRows}
                                total={100}
                                empty="No department split for this period."
                                colorFor={(row) => (
                                    row.presentPercent >= 85 ? "bg-emerald-500"
                                        : row.presentPercent >= 70 ? "bg-amber-500"
                                            : "bg-rose-500"
                                )}
                            />
                        </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 p-3.5 dark:border-gray-700">
                        <div className="mb-2 flex flex-wrap items-end justify-between gap-x-3 gap-y-1">
                            <SectionLabel>Needs attention</SectionLabel>
                            <p className="mb-1.5 text-[11px] text-muted-foreground">
                                One row per person per issue · click opens the latest date
                            </p>
                        </div>
                        <div className="mb-3 flex flex-wrap gap-1.5">
                            {ISSUE_LEGEND.map((item) => (
                                <Tooltip key={item.key}>
                                    <TooltipTrigger>
                                        <span className={cn("inline-flex cursor-help rounded-full px-2 py-0.5 text-[11px] font-medium", item.chip)}>
                                            {item.label}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-xs text-xs">
                                        {item.rule}
                                    </TooltipContent>
                                </Tooltip>
                            ))}
                        </div>
                        {(data?.attention || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                Nothing needs a decision in this period.
                            </p>
                        ) : (
                            <>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[640px] text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-left dark:border-gray-800">
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Employee</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Latest</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Issue</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Shift</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Late</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.attention.map((row, index) => {
                                            const legend = ISSUE_LEGEND.find((item) => item.key === row.issue);
                                            return (
                                            <tr
                                                key={`${row.issue}-${row.attendance_id || row.employee?.id}-${row.date}-${index}`}
                                                className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/40"
                                                onClick={() => openAttention(row)}
                                            >
                                                <td className="py-2">
                                                    <span className="font-medium text-gray-900 dark:text-white">
                                                        {row.employee?.employee_name || "—"}
                                                    </span>
                                                    <span className="ml-2 text-xs text-muted-foreground">
                                                        {row.employee?.employee_id || ""}
                                                    </span>
                                                </td>
                                                <td className="py-2 whitespace-nowrap">
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <span className="cursor-help border-b border-dotted border-gray-400 text-gray-900 dark:border-gray-500 dark:text-white">
                                                                {fmtDate(row.date)}
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top" sideOffset={8} maxWidth="360px" className="text-xs leading-relaxed">
                                                            {latestTooltip(row)}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </td>
                                                <td className="py-2">
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <span className={cn(
                                                                "inline-flex cursor-help rounded-full px-2 py-0.5 text-[11px] font-medium",
                                                                legend?.chip || "bg-gray-100 text-gray-700",
                                                            )}>
                                                                {row.label}
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top" sideOffset={8} maxWidth="280px" className="text-xs leading-relaxed">
                                                            {legend?.rule || row.label}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </td>
                                                <td className="py-2 text-muted-foreground">{row.shift || "—"}</td>
                                                <td className="py-2 tabular-nums">
                                                    {row.late_minutes > 0 ? `${row.late_minutes} min` : "—"}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {data.attention_total > data.attention.length ? (
                                <p className="mt-2 text-[11px] text-muted-foreground">
                                    Showing {data.attention.length} of {data.attention_total}. Open Day Attendance or Punch Discrepancies for the rest.
                                </p>
                            ) : null}
                            </>
                        )}
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default AttendanceDashboard;
